/* Storefront disclosure only. Checkout/API enforcement requires a server-side validation app. */
(() => {
  'use strict';
  const cartGuard = '[data-sm-special-order-cart]';
  const checkoutForms = 'form[data-sm-cart-form]';
  const pending = new WeakSet();
  const permitted = new WeakSet();
  let revision = 0;
  let pendingCartRequests = 0;

  function state(guard) {
    return JSON.stringify(Array.from(guard.querySelectorAll('[data-sm-cart-line]'), line => ({
      key: line.dataset.key, variant: line.dataset.variant, quantity: line.dataset.quantity,
      special: line.dataset.special, estimate: line.dataset.estimate
    })).sort((a, b) => a.key.localeCompare(b.key)));
  }
  function showError(guard, message) {
    const error = guard.querySelector('[data-sm-cart-error]');
    if (error) { error.textContent = message; error.hidden = false; error.tabIndex = -1; error.focus(); }
  }
  function invalidate() {
    revision += 1;
    document.querySelectorAll('[data-sm-cart-ack]').forEach(input => { input.checked = false; });
  }
  function hideAcceleratedCartButtons() {
    document.querySelectorAll('form[data-sm-cart-form] .additional-checkout-buttons').forEach(element => {
      element.hidden = true;
      element.inert = true;
    });
  }
  // Count the actual requests: unrelated theme loading events cannot unlock checkout,
  // and one completed request cannot unlock another cart mutation still in flight.
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = args[0];
    let mutation = false;
    try {
      const requestUrl = new URL(typeof request === 'string' || request instanceof URL ? request : request.url, window.location.origin);
      const method = (args[1]?.method || request?.method || 'GET').toUpperCase();
      mutation = requestUrl.origin === window.location.origin && method !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(requestUrl.pathname);
    } catch (_) { /* Preserve native fetch behavior for unrecognized inputs. */ }
    if (!mutation) return originalFetch.apply(this, args);
    pendingCartRequests += 1;
    hideAcceleratedCartButtons();
    invalidate();
    const complete = () => { pendingCartRequests -= 1; invalidate(); };
    try { return originalFetch.apply(this, args).finally(complete); }
    catch (error) { complete(); throw error; }
  };
  document.addEventListener('cart:prepare-bundled-sections', () => { hideAcceleratedCartButtons(); invalidate(); });
  ['cart:change', 'cart:refresh', 'cart:error', 'cart-drawer:refreshed'].forEach(name => {
    document.addEventListener(name, invalidate);
  });
  window.addEventListener('pageshow', invalidate);
  window.addEventListener('focus', () => { hideAcceleratedCartButtons(); invalidate(); });
  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof HTMLElement)) return;
    if (input.closest('line-item, quick-order-list') || input.matches('[name="updates[]"]')) invalidate();
    const form = input.form;
    if (form && !input.matches('[data-sm-product-ack]') && (/^(id|quantity|options\[)/.test(input.name || '') || input.matches('[data-option-position]'))) {
      form.querySelectorAll('[data-sm-product-ack]').forEach(checkbox => { checkbox.checked = false; });
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const unavailable = form.querySelector('[data-sm-special-order-product][data-dispatch-confirmed="false"]');
    if (form.closest('product-form') && unavailable) {
      event.preventDefault(); event.stopImmediatePropagation(); unavailable.focus(); return;
    }
    if (!form.matches(checkoutForms)) return;
    if (event.submitter?.name !== 'checkout') return;
    if (permitted.has(form)) { permitted.delete(form); return; }
    const guard = form.querySelector(cartGuard);
    if (!guard) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending.has(form)) return;
    if (pendingCartRequests > 0) { showError(guard, guard.dataset.errorUpdating || 'Your cart is updating. Please review it and acknowledge the dispatch estimates again.'); return; }
    if (guard.dataset.status === 'unknown') { showError(guard, guard.dataset.errorUnknown || 'Please remove the item without a confirmed dispatch estimate or contact us before checking out.'); return; }
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const checked = guard.querySelector('[data-sm-cart-ack]');
    if (checked && !checked.checked) { checked.reportValidity(); return; }
    const submittedRevision = revision;
    const submittedState = state(guard);
    const submitter = event.submitter;
    pending.add(form);
    submitter.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(guard.dataset.cartUrl, window.location.origin);
      url.searchParams.set('section_id', guard.dataset.sectionId);
      url.searchParams.set('_sm_review', String(Date.now()));
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart refresh failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector(cartGuard);
      if (!fresh) throw new Error('Cart details are missing');
      if (pendingCartRequests > 0 || revision !== submittedRevision || state(fresh) !== submittedState || fresh.dataset.status !== guard.dataset.status) {
        invalidate();
        const reviewUrl = new URL(guard.dataset.cartUrl, window.location.origin);
        reviewUrl.searchParams.set('special_order_review', '1');
        window.location.assign(reviewUrl.toString());
        return;
      }
      if (fresh.dataset.status === 'unknown') throw new Error('Dispatch time is not confirmed');
      if (!form.isConnected || !form.checkValidity() || (checked && !checked.checked)) return;
      permitted.add(form);
      HTMLFormElement.prototype.requestSubmit.call(form, submitter);
    } catch (error) {
      invalidate();
      showError(guard, guard.dataset.errorFailed || 'We could not confirm the latest cart and dispatch details. Please refresh your cart and try again.');
    } finally {
      window.clearTimeout(timeout);
      pending.delete(form);
      submitter.removeAttribute('aria-busy');
    }
  }, true);
  function showReviewMessage() {
    if (new URLSearchParams(window.location.search).get('special_order_review') !== '1') return;
    document.querySelectorAll(cartGuard).forEach(guard => {
      showError(guard, guard.dataset.errorChanged || 'Your cart or a dispatch estimate changed. Please review the current details and acknowledge the dispatch estimates again.');
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', showReviewMessage, { once: true });
  else showReviewMessage();
})();
